import { createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { apiClient } from "@/lib/api-client";

export const Route = createFileRoute("/_layout/buy")({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			code: (search.code as string) || undefined,
		};
	},
	component: BuyPage,
});

const features = [
	{
		title: "Create Your Own Radio Station",
		description: "Broadcast live to listeners anywhere in the world",
	},
	{
		title: "Stream from Any App",
		description: "Share audio from any application you love on your Mac",
	},
	{
		title: "Chat with Listeners",
		description: "Foster community through real-time conversation",
	},
	{
		title: "Get a Shareable Link",
		description: "Instant listeners with one click - no app download required",
	},
	{
		title: "On-Air Notifications",
		description: "Bring fans back when you go live with push notifications",
	},
	{
		title: "Local Audio Recording",
		description: "Save your broadcasts for later sharing. Coming soon",
	},
];

function ComparisonSection() {
	return (
		<div className="border-t border-gray-200 pt-16">
			<h2 className="font-serif-600 text-3xl md:text-4xl text-black text-center mb-12">
				Not convinced?
			</h2>
			<div className="grid md:grid-cols-2 gap-12">
				<div className="text-center">
					<h3 className="font-serif-600 text-xl mb-6 text-black">
						What you get
					</h3>
					<ul className="space-y-4 font-serif text-base md:text-lg text-gray-600 leading-relaxed">
						<li>Hosted station with unlimited listeners</li>
						<li>One-button setup, no technical knowledge needed</li>
						<li>Built-in chat room for your community</li>
						<li>iOS broadcaster included</li>
						<li>All future updates</li>
					</ul>
				</div>
				<div className="text-center">
					<h3 className="font-serif-600 text-xl mb-6 text-black">
						The alternative
					</h3>
					<ul className="space-y-4 font-serif text-base md:text-lg text-gray-600 leading-relaxed">
						<li>Shoutcast/Icecast server: $60/year</li>
						<li>Loopback for audio routing: $99</li>
						<li>Audio Hijack for recording: $69</li>
						<li>OBS setup and configuration</li>
						<li>No integrated chat solution</li>
						<li className="font-semibold text-black pt-3 text-lg md:text-xl">
							Total: $168 + $60/year
						</li>
					</ul>
				</div>
			</div>
			<p className="font-serif text-center mt-12 text-xl text-gray-600">
				Macrowave bundles everything for{" "}
				<span className="font-semibold text-black">$119</span>, once.
			</p>
		</div>
	);
}

function BuyPage() {
	const { code } = Route.useSearch();
	const [isLoading, setIsLoading] = useState(false);

	const handleBuy = async () => {
		setIsLoading(true);
		try {
			const response = await apiClient.checkout.createSession({
				couponCode: code,
			});
			window.location.href = response.sessionUrl;
		} catch (error) {
			setIsLoading(false);
			console.error("Failed to create checkout session:", error);
		}
	};

	return (
		<div className="min-h-screen py-16 px-4">
			<div className="max-w-4xl mx-auto">
				{/* Hero Section */}
				<div className="text-center mb-16">
					<h1 className="font-serif-600 text-4xl md:text-5xl text-black mb-6">
						Macrowave Broadcaster
					</h1>
					<p className="font-serif text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
						Turn your Mac or iPhone into a radio station. Stream audio from
						any app, chat with listeners, and build your audience.
					</p>
					<div className="inline-block">
						<span className="font-serif-600 text-5xl md:text-6xl text-black">
							$119
						</span>
						<p className="font-serif text-gray-500 mt-2">
							One-time payment. Valid for life.
						</p>
						<p className="font-serif text-gray-500">
							Free trial available in the app.
						</p>
						<a
							href="https://apps.apple.com/app/apple-store/id6746954963?pt=123127751&ct=macrowave.co&mt=8"
							target="_blank"
							rel="noopener noreferrer"
							className="inline-block mt-4"
						>
							<img
								src="/images/app-store-badge.svg"
								alt="Download on the App Store"
								width="120"
								height="40"
								className="h-10"
							/>
						</a>
					</div>
				</div>

				{/* Video + iPhone Row */}
				<div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-16">
					<div className="flex-shrink-0">
						<video
							autoPlay
							loop
							muted
							playsInline
							poster="/images/demo.avif"
							className="w-64 md:w-80 h-auto rounded-lg"
							width="970"
							height="970"
						>
							<source src="/videos/demo.mp4" type="video/mp4" />
							<img
								src="/images/demo.webp"
								alt="Macrowave demo"
								className="w-64 md:w-80 h-auto rounded-lg"
								width="970"
								height="970"
							/>
						</video>
					</div>
					<div className="flex-shrink-0">
						<img
							src="/images/iphone-broadcaster.webp"
							alt="Macrowave iPhone receiver app"
							className="w-auto h-64 md:h-80 rounded-2xl"
							width="1240"
							height="2500"
							loading="lazy"
						/>
					</div>
				</div>

				{/* Features Grid */}
				<div className="grid gap-x-8 gap-y-12 pt-12 md:grid-cols-3 mb-16">
					{features.map((feature) => (
						<div key={feature.title} className="text-center">
							<h3 className="font-serif font-semibold text-xl mb-2 text-black">
								{feature.title}
							</h3>
							<p className="font-serif text-gray-600 leading-relaxed">
								{feature.description}
							</p>
						</div>
					))}
				</div>

				{/* CTA Section */}
				<div className="text-center mb-16">
					<button
						onClick={handleBuy}
						disabled={isLoading}
						className="inline-flex items-center justify-center px-8 py-4 bg-black text-white font-serif font-semibold text-lg rounded-lg hover:bg-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
					>
						{isLoading ? (
							<>
								<Loader2 className="w-5 h-5 mr-2 animate-spin" />
								Loading...
							</>
						) : (
							"Buy Now"
						)}
					</button>
					<p className="font-serif text-gray-500 mt-4">
						Requires macOS 14.4+ or iOS 18+
					</p>
				</div>

				<ComparisonSection />
			</div>
		</div>
	);
}
